import { useMemo, useState } from 'react';
import { Application, type OrgUser } from '@core/types';
import type { ApplicationStatus } from '@core/types';
import { PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { CandidateApplicationDetailModal } from '@modules/candidates/CandidateApplicationDetailModal';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

function ago(iso: string): string {
  try {
    const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d < 1) return 'today';
    if (d === 1) return 'yesterday';
    if (d < 14) return `${d}d ago`;
    if (d < 60) return `${Math.floor(d / 7)}w ago`;
    return `${Math.floor(d / 30)}mo ago`;
  } catch {
    return '—';
  }
}

const TERMINAL: ApplicationStatus[] = ['placed', 'rejected', 'closed', 'withdrawn'];

const DT = 'vscode-text-secondary pr-2 min-w-0 text-sm';
const DD = 'vscode-text-primary min-w-0 break-words text-sm';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className={DT}>{label}</dt>
      <dd className={DD}>{value}</dd>
    </>
  );
}

type Props = {
  applications: Application[];
  teamMembers: OrgUser[];
};

/**
 * Left-column content for Data → Jobs → [job] → Applications tab.
 * Mirrors {@link CandidateApplicationsPanel} (candidate profile Applications tab).
 */
export function JobApplicationsPanel({ applications, teamMembers }: Props) {
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);

  const sorted = useMemo(
    () => [...applications].sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()),
    [applications]
  );

  const stats = useMemo(() => {
    const active = sorted.filter((a) => !TERMINAL.includes(a.status as ApplicationStatus)).length;
    const placed = sorted.filter((a) => a.status === 'placed').length;
    const rejected = sorted.filter((a) => a.status === 'rejected').length;
    const withdrawn = sorted.filter((a) => a.status === 'withdrawn').length;
    const closed = sorted.filter((a) => a.status === 'closed').length;
    const screening = sorted.filter((a) =>
      ['sourced', 'applied', 'screening', 'screened'].includes(a.status)
    ).length;
    const shortlisted = sorted.filter((a) => a.status === 'shortlisted').length;
    const interviewing = sorted.filter((a) =>
      ['interview_1_scheduled','interview_1_conducted','interview_2_scheduled','interview_2_conducted'].includes(a.status)
    ).length;
    const offer = sorted.filter((a) =>
      ['offer_extended', 'offer_accepted'].includes(a.status)
    ).length;
    const staleCount = sorted.filter((a) => {
      if (TERMINAL.includes(a.status as ApplicationStatus)) return false;
      const d = Math.floor((Date.now() - new Date(a.status_updated_at).getTime()) / 86400000);
      return d >= 5;
    }).length;
    const fitVals = sorted.map((a) => a.fitment_score).filter((n): n is number => n != null);
    const avgFit = fitVals.length > 0 ? Math.round(fitVals.reduce((s, n) => s + n, 0) / fitVals.length) : null;
    const maxFit = fitVals.length > 0 ? Math.max(...fitVals) : null;
    const uniqueCandidates = new Set(sorted.map((a) => a.candidate_id).filter(Boolean)).size;
    const lastSub = sorted[0]?.submitted_at ?? null;
    const stKeys = sorted.map((a) => new Date(a.status_updated_at).getTime()).filter(Boolean);
    const lastStage = stKeys.length > 0 ? new Date(Math.max(...stKeys)).toISOString() : null;
    const withFitment = sorted.filter((a) => a.fitment_score != null).length;
    const withAts = sorted.filter((a) => (a as unknown as { optimization?: unknown }).optimization != null).length;
    const byStatus: Record<string, number> = {};
    for (const a of sorted) byStatus[a.status] = (byStatus[a.status] ?? 0) + 1;
    return {
      total: sorted.length,
      active,
      placed,
      rejected,
      withdrawn,
      closed,
      screening,
      shortlisted,
      interviewing,
      offer,
      staleCount,
      avgFit,
      maxFit,
      withFitment,
      withAts,
      uniqueCandidates,
      lastSub,
      lastStage,
      byStatus,
    };
  }, [sorted]);

  if (sorted.length === 0) {
    return (
      <div className="ggj-profile-section-body py-10 text-center">
        <p className="text-sm vscode-text-secondary font-medium">No applications for this job yet.</p>
        <p className="text-xs vscode-text-tertiary mt-1">Submit a candidate to this requisition to see pipeline stats here.</p>
      </div>
    );
  }

  return (
    <>
      <div className="ggj-profile-section-h">Pipeline summary</div>
      <div className="ggj-profile-section-body">
        <dl className="ggj-attr-grid">
          <Row label="Total applications" value={stats.total} />
          <Row label="Active in pipeline" value={stats.active} />
          <Row label="Unique candidates" value={stats.uniqueCandidates} />
          <Row label="Placed" value={stats.placed} />
          <Row label="Rejected" value={stats.rejected} />
          <Row label="Withdrawn / closed" value={`${stats.withdrawn} / ${stats.closed}`} />
          <Row label="Latest submission" value={stats.lastSub ? ago(stats.lastSub) : '—'} />
          <Row label="Last stage change" value={stats.lastStage ? ago(stats.lastStage) : '—'} />
        </dl>
      </div>

      <div className="ggj-profile-section-h">Pipeline progress</div>
      <div className="ggj-profile-section-body">
        <dl className="ggj-attr-grid">
          <Row label="Screening / sourced"
            value={
              <span className="inline-flex items-center gap-2">
                <span className="font-semibold tabular-nums">{stats.screening}</span>
                {stats.screening > 0 && <StatusBadge status="screening" />}
              </span>
            }
          />
          <Row label="Shortlisted"
            value={
              <span className="inline-flex items-center gap-2">
                <span className="font-semibold tabular-nums">{stats.shortlisted}</span>
                {stats.shortlisted > 0 && <StatusBadge status="shortlisted" />}
              </span>
            }
          />
          <Row label="Interviewing"
            value={
              <span className="inline-flex items-center gap-2">
                <span className="font-semibold tabular-nums">{stats.interviewing}</span>
                {stats.interviewing > 0 && <StatusBadge status="interview_1_scheduled" />}
              </span>
            }
          />
          <Row label="Offer stage"
            value={
              <span className="inline-flex items-center gap-2">
                <span className="font-semibold tabular-nums">{stats.offer}</span>
                {stats.offer > 0 && <StatusBadge status="offer_extended" />}
              </span>
            }
          />
          <Row
            label="Stale (≥ 5d)"
            value={
              stats.staleCount > 0 ? (
                <span className="font-semibold tabular-nums text-amber-500">{stats.staleCount}</span>
              ) : (
                <span className="font-semibold tabular-nums vscode-text-primary">0</span>
              )
            }
          />
        </dl>
      </div>

      <div className="ggj-profile-section-h">Stage distribution</div>
      <div className="ggj-profile-section-body">
        <dl className="ggj-attr-grid">
          {Object.entries(stats.byStatus)
            .sort((a, b) => b[1] - a[1])
            .map(([st, n]) => (
              <Row
                key={st}
                label={PIPELINE_STATUS_LABEL[st as ApplicationStatus] ?? st}
                value={
                  <span className="inline-flex items-center gap-2">
                    <span className="font-semibold tabular-nums">{n}</span>
                    <StatusBadge status={st as ApplicationStatus} />
                  </span>
                }
              />
            ))}
        </dl>
      </div>

      <div className="ggj-profile-section-h">Fitment &amp; scoring</div>
      <div className="ggj-profile-section-body">
        <dl className="ggj-attr-grid">
          <Row label="Avg. role-fit score" value={stats.avgFit != null ? `${stats.avgFit} / 100` : '—'} />
          <Row label="Highest fitment score" value={stats.maxFit != null ? `${stats.maxFit} / 100` : '—'} />
          <Row label="With fitment data" value={`${stats.withFitment} of ${stats.total}`} />
          <Row label="ATS optimized" value={`${stats.withAts} of ${stats.total}`} />
        </dl>
      </div>

      <div className="ggj-profile-section-h">Submitted applications</div>
      <div className="ggj-profile-section-body">
        <dl className="ggj-attr-grid">
          <dt className={`${DT} lg:col-span-1`}>Submitted applications</dt>
          <dd className={`${DD} lg:col-span-3 space-y-1.5`}>
            {sorted.map((app) => (
              <button
                key={app.id}
                type="button"
                onClick={() => setSelectedApp(app)}
                className="flex items-baseline gap-1.5 text-left text-sm text-[var(--vscode-primary)] hover:underline w-full"
              >
                <span className="font-semibold truncate shrink-0">{app.candidate?.full_name ?? '—'}</span>
                <span className="vscode-text-tertiary text-xs shrink-0">›</span>
                <span className="vscode-text-secondary text-xs truncate">
                  {PIPELINE_STATUS_LABEL[app.status as ApplicationStatus] ?? app.status}
                </span>
                <span className="vscode-text-tertiary text-[10px] shrink-0 ml-auto tabular-nums">
                  {formatDateTimeGmt(app.submitted_at)}
                </span>
              </button>
            ))}
          </dd>
        </dl>
      </div>

      {selectedApp && (
        <CandidateApplicationDetailModal
          app={selectedApp}
          teamMembers={teamMembers}
          onClose={() => setSelectedApp(null)}
        />
      )}
    </>
  );
}
